import * as moment from "moment";

export type DayScheduleStr = string;

export interface WeeklyHours {
    weekly_hours_id?: number;
    mon?: DayScheduleStr;
    tue?: DayScheduleStr;
    wed?: DayScheduleStr;
    thu?: DayScheduleStr;
    fri?: DayScheduleStr;
    sat?: DayScheduleStr;
    sun?: DayScheduleStr;
}

export interface TimeSpan {
    startHour?: number;
    startMinute?: number;
    endHour?: number;
    endMinute?: number;
}

export interface DaySchedule {
    timeSpans?: Array<TimeSpan>;
}

export function getSameDaySchedule(weeklyHours: WeeklyHours, time: moment.Moment): DaySchedule {
    if (!weeklyHours || !time) return null;
    const day = time.day(); // 0-sunday -> 6-saturday
    let hours: DayScheduleStr = null;
    if (day === 0) hours = weeklyHours.sun;
    else if (day === 1) hours = weeklyHours.mon;
    else if (day === 2) hours = weeklyHours.tue;
    else if (day === 3) hours = weeklyHours.wed;
    else if (day === 4) hours = weeklyHours.thu;
    else if (day === 5) hours = weeklyHours.fri;
    else if (day === 6) hours = weeklyHours.sat;
    return parseDaySchedule(hours);
}

export function getNextDate(weeklyHours: WeeklyHours, time: moment.Moment): moment.Moment {
    if (!weeklyHours || !time) return null;
    const startDay = time.day(); // 0-sunday -> 6-saturday
    for (let i = 0; i < 7; i++) {
        let tmp: DayScheduleStr = null;
        const day = (i + startDay) % 7;
        if (day === 0) tmp = weeklyHours.sun;
        else if (day === 1) tmp = weeklyHours.mon;
        else if (day === 2) tmp = weeklyHours.tue;
        else if (day === 3) tmp = weeklyHours.wed;
        else if (day === 4) tmp = weeklyHours.thu;
        else if (day === 5) tmp = weeklyHours.fri;
        else if (day === 6) tmp = weeklyHours.sat;
        if (tmp) {
            const daySchedule = parseDaySchedule(tmp);
            if (i > 0) return time.clone().add(i, "days");
            else {
                // make sure there is an end time that is before now
                for (const timeSpan of daySchedule.timeSpans) {
                    if (isBeforeTimeSpan(timeSpan, time)) return time;
                }
            }
        }
    }
    return null;
}

export function getNextDaySchedule(weeklyHours: WeeklyHours, time: moment.Moment): DaySchedule {
    const nextDate = getNextDate(weeklyHours, time);
    return getSameDaySchedule(weeklyHours, nextDate);
}

export function isWithinWeeklyHours(weeklyHours: WeeklyHours, time: moment.Moment) {
    if (!weeklyHours || !time) return true;
    const daySchedule = getSameDaySchedule(weeklyHours, time);
    return isWithinDaySchedule(daySchedule, time);
}

export function isWithinDaySchedule(daySchedule: DaySchedule, time: moment.Moment) {
    if (!daySchedule || !time) return true;
    for (const timeSpan of daySchedule.timeSpans) {
        if (isWithinTimeSpan(timeSpan, time)) return true;
    }
    return false;
}

export function isWithinTimeSpan(timeSpan: TimeSpan, time: moment.Moment) {
    if (!timeSpan || !time) return true;
    const hour = time.hour();
    const minute = time.minute();
    if (hour < timeSpan.startHour) return false;
    if (hour > timeSpan.endHour) return false;
    if (hour === timeSpan.startHour && minute < timeSpan.startMinute) return false;
    if (hour === timeSpan.endHour && minute > timeSpan.endMinute) return false;
    return true;
}

export function isBeforeTimeSpan(timeSpan: TimeSpan, time: moment.Moment) {
    if (!timeSpan || !time) return true;
    const hour = time.hour();
    const minute = time.minute();
    if (hour > timeSpan.endHour) return false;
    if (hour === timeSpan.endHour && minute > timeSpan.endMinute) return false;
    return true;
}

export function toDaySchedule(daySchedule: DayScheduleStr): DaySchedule {
    if (daySchedule === null || (typeof daySchedule !== "undefined" && daySchedule.length === 0)) {
        return null;
    }
    return parseDaySchedule(daySchedule);
}

export function toDayScheduleStr(daySchedule: DayScheduleStr | DaySchedule): DayScheduleStr {
    if (daySchedule === null) {
        return null;
    }
    if (!daySchedule) {
        return null;
    }
    if (typeof daySchedule === "string") {
        return daySchedule;
    } else {
        return formatDaySchedule(daySchedule);
    }
}

export function toWeeklyHoursCouponString(weeklyHours: WeeklyHours) {
    if (weeklyHours == null) {
        return "";
    }

    const days: Array<{ day: string, schedule: DaySchedule }> = [
        {day: "Monday", schedule: toDaySchedule(weeklyHours.mon)},
        {day: "Tuesday", schedule: toDaySchedule(weeklyHours.tue)},
        {day: "Wednesday", schedule: toDaySchedule(weeklyHours.wed)},
        {day: "Thursday", schedule: toDaySchedule(weeklyHours.thu)},
        {day: "Friday", schedule: toDaySchedule(weeklyHours.fri)},
        {day: "Saturday", schedule: toDaySchedule(weeklyHours.sat)},
        {day: "Sunday", schedule: toDaySchedule(weeklyHours.sun)}
    ];

    const formatDayRange = (lastString, printDays, formatted): string => {
        sb = lastString;
        if (!formatted) {
            return sb;
        }
        if (sb.length > 0) {
            sb = sb + "; or ";
        }
        sb = sb + formatted;
        if (printDays.length === 7) {
            sb = sb + "";
        } else if (printDays.length === 1) {
            sb = sb + " " + printDays[0].day;
        } else if (printDays.length === 2) {
            sb = sb + " " + printDays[0].day + " and " + printDays[1].day;
        } else if (printDays.length > 0) {
            sb = sb + " " + printDays[0].day + " through " + printDays[printDays.length - 1].day;
        }
        return sb;
    };

    let sb = "";
    let formattedDays = [];
    let lastFormatted = null;
    days.forEach((d) => {
        const formatted = formatDayScheduleForCoupon(d.schedule);
        if (formatted && (!lastFormatted || lastFormatted === formatted)) {
            formattedDays.push(d);
        } else {
            if (formattedDays.length > 0 && lastFormatted) {
                // output this sequence start/end
                sb = formatDayRange(sb, formattedDays, lastFormatted);
            }
            formattedDays = [];
            if (formatted) {
                formattedDays.push(d);
            }
        }
        lastFormatted = formatted;
    });
    sb = formatDayRange(sb, formattedDays, lastFormatted);
    return sb;
}

function parseDaySchedule(daySchedule: string): DaySchedule {
    if (daySchedule === null) return null;

    const ds: DaySchedule = {
        timeSpans: []
    };
    daySchedule = daySchedule.trim();
    const times = daySchedule.split(",");
    times.forEach((ts) => {
        if (ts) {
            try {
                ds.timeSpans.push(parseTimeSpan(ts));
            } catch (error) {
                // ignore parse error...
            }
        }
    });
    ds.timeSpans = ds.timeSpans.sort((n1, n2) => {
        let rs = n1.startHour - n2.startHour;
        if (rs === 0) {
            rs = n1.startMinute - n2.startMinute;
        }
        return rs;
    });
    return ds;
}

function parseTimeSpan(timeSpan: string): TimeSpan {
    const ts: TimeSpan = {};
    timeSpan = timeSpan.trim();
    const times = timeSpan.split("-");
    parseIntoTimeSpan(times[0].trim(), true, parseAMPM(times[1].trim()), ts);
    parseIntoTimeSpan(times[1].trim(), false, parseAMPM(times[0].trim()), ts);
    return ts;
}

function parseAMPM(time: string): string {
    let tmp: string = "";
    let stage: number = 0;
    for (let i = 0; i < time.length; i++) {
        const c = time.charAt(i);
        if (stage === 0 && c === ':') {
            tmp = "";
            stage = 1;
        } else if (stage < 2 && (/[a-zA-Z]/).test(c)) {
            tmp = "" + c;
            stage = 2;
        } else {
            tmp += c;
        }
    }
    if (stage === 0) {
        // do nothing
    } else if (stage === 1) {
        // do nothing
    } else if (stage === 2) {
        return tmp.trim().toLowerCase();
    }
    return null;
}

function parseIntoTimeSpan(time: string, start: boolean, otherAMPM: string, ts: TimeSpan): void {
    let hour: number = 0;
    let minute: number = 0;
    let pm: boolean = null;

    let tmp: string = "";
    let stage: number = 0;
    for (let i = 0; i < time.length; i++) {
        const c = time.charAt(i);
        if (stage === 0 && c === ':') {
            hour = Number(tmp.trim());
            tmp = "";
            stage = 1;
        } else if (stage < 2 && (/[a-zA-Z]/).test(c)) {
            if (stage === 0) {
                hour = Number(tmp.trim());
            } else if (stage === 1) {
                minute = Number(tmp.trim());
            }
            tmp = "" + c;
            stage = 2;
        } else {
            tmp += c;
        }
    }
    if (stage === 0) {
        hour = Number(tmp.trim());
    } else if (stage === 1) {
        minute = Number(tmp.trim());
    } else if (stage === 2) {
        pm = tmp.trim().toLowerCase().startsWith("p");
    }

    if (pm === null && otherAMPM) {
        pm = otherAMPM.trim().toLowerCase().startsWith("p");
    }
    if (!pm && hour === 12) {
        hour -= 12;
    } else if (pm && hour < 12) {
        hour += 12;
    }

    if (start) {
        ts.startHour = hour;
        ts.startMinute = minute;
    } else {
        ts.endHour = hour;
        ts.endMinute = minute;
    }
}

function formatDaySchedule(daySchedule: DaySchedule): string {
    let sb = "";
    daySchedule.timeSpans.forEach((ts) => {
        if (sb.length > 0) {
            sb = sb + ", ";
        }
        sb = sb + formatTimeSpan(ts);
    });
    return sb;
}

function formatTimeSpan(timeSpan: TimeSpan): string {
    let sb = "";
    const ampm1 = timeSpan.startHour < 12 ? "AM" : "PM";
    let h1 = timeSpan.startHour % 12;
    if (h1 === 0) {
        h1 = 12;
    }
    const ampm2 = timeSpan.endHour < 12 ? "AM" : "PM";
    let h2 = timeSpan.endHour % 12;
    if (h2 === 0) {
        h2 = 12;
    }

    sb = sb + String(h1);
    if (timeSpan.startMinute !== 0) {
        sb = sb + ":";
        sb = sb + (timeSpan.startMinute < 10 ? 0 : "");
        sb = sb + (timeSpan.startMinute);
    }
    sb = sb + ampm1;
    sb = sb + "-";
    sb = sb + h2;
    if (timeSpan.endMinute !== 0) {
        sb = sb + ":";
        sb = sb + (timeSpan.endMinute < 10 ? 0 : "");
        sb = sb + (timeSpan.endMinute);
    }
    sb = sb + ampm2;
    return sb;
}

function formatDayScheduleForCoupon(daySchedule: DaySchedule): string {
    if (daySchedule === null) {
        return "";
    }
    let sb = "";
    let count = 0;
    if (daySchedule.timeSpans) {
        daySchedule.timeSpans.forEach((ts) => {
            if (count > 0) {
                if (count + 1 === daySchedule.timeSpans.length) {
                    sb = sb + ", and ";
                } else {
                    sb = sb + ", ";
                }
            }
            sb = sb + formatTimeSpanForCoupon(ts);
            count++;
        });
    }
    // console.log(sb);
    return sb;
}

function formatTimeSpanForCoupon(timeSpan: TimeSpan): string {
    let sb = "";
    const ampm1 = timeSpan.startHour < 12 ? "AM" : "PM";
    let h1 = timeSpan.startHour % 12;
    if (h1 === 0) {
        h1 = 12;
    }
    const ampm2 = timeSpan.endHour < 12 ? "AM" : "PM";
    let h2 = timeSpan.endHour % 12;
    if (h2 === 0) {
        h2 = 12;
    }

    sb = sb + String(h1);
    if (timeSpan.startMinute !== 0) {
        sb = sb + ":";
        sb = sb + (timeSpan.startMinute < 10 ? 0 : "");
        sb = sb + (timeSpan.startMinute);
    }
    sb = sb + ampm1;
    sb = sb + "-";
    sb = sb + h2;
    if (timeSpan.endMinute !== 0) {
        sb = sb + ":";
        sb = sb + (timeSpan.endMinute < 10 ? 0 : "");
        sb = sb + (timeSpan.endMinute);
    }
    sb = sb + ampm2;
    return sb;
}
